import type { Component, Tone } from '../src/platform/types.ts';

export type Counter = { offsetMs: () => number };

let clock: () => number = () => Date.now();
/** Install a function returning the estimated server time (ms since epoch). */
export function setClock(fn: () => number): void {
  clock = fn;
}

/** The estimated server time: phones in a hall pulse together when they all read this clock. */
export const serverNow = (): number => clock();

type Attrs = Record<string, string | undefined>;

export function h(tag: string, cls?: string, text?: string, attrs?: Attrs): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  if (attrs) for (const k of Object.keys(attrs)) { const v = attrs[k]; if (v !== undefined) e.setAttribute(k, v); }
  return e;
}

function add(parent: HTMLElement, ...kids: (HTMLElement | null | undefined)[]): HTMLElement {
  for (const k of kids) if (k) parent.appendChild(k);
  return parent;
}

const SPEAKER_CLASS: Record<string, string> = { MOD: 'sp-mod', ARIA: 'sp-aria' };

function linesBlock(lines: { speaker: string; text: string }[], cls = 'c-lines'): HTMLElement {
  const box = h('div', cls);
  for (const l of lines) {
    const row = h('div', 'line ' + (SPEAKER_CLASS[l.speaker] ?? 'sp-other'));
    add(row, h('span', 'speaker', l.speaker), h('span', 'say', l.text));
    box.appendChild(row);
  }
  return box;
}

function heading(title?: string): HTMLElement | null {
  return title ? h('h3', 'c-h', title) : null;
}

const ACTOR_ICON: Record<string, string> = { agent: '🤖', human: '👥' };
const MODE_LABEL: Record<string, string> = { serbest: 'canlı ajan', sinirli: 'canlı ajan', sablon: 'şablon', script: 'hazır metin' };
const EVIDENCE = ['söylendi', 'görüldü', 'denendi', 'ölçüldü'];

function countdown(closesAt: string): HTMLElement {
  const el = h('span', 'countdown');
  const end = Date.parse(closesAt);
  const tick = () => {
    if (!el.isConnected && el.dataset.seen) { clearInterval(timer); return; }
    if (el.isConnected) el.dataset.seen = '1';
    const left = Math.max(0, Math.round((end - clock()) / 1000));
    el.textContent = left > 0 ? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` : 'süre doldu';
    el.classList.toggle('urgent', left > 0 && left <= 10);
  };
  const timer = setInterval(tick, 500);
  tick();
  return el;
}
export { countdown };

function renderOne(c: Component): HTMLElement {
  switch (c.kind) {
    case 'title': {
      const e = h('div', 'c c-title');
      add(e, c.eyebrow ? h('div', 'eyebrow', c.eyebrow) : null, h('h1', '', c.title), c.subtitle ? h('div', 'subtitle', c.subtitle) : null);
      return e;
    }
    case 'lines':
      return add(h('div', 'c'), linesBlock(c.lines));
    case 'kpis': {
      const e = h('div', 'c c-kpis');
      for (const k of c.items) {
        const t = h('div', 'kpi band-' + (k.band ?? 'neutral'));
        add(t, h('div', 'kpi-label', k.label), h('div', 'kpi-value', k.value), k.delta ? h('div', 'kpi-delta', k.delta) : null);
        e.appendChild(t);
      }
      return e;
    }
    case 'cards': {
      const e = h('div', 'c');
      add(e, heading(c.title));
      const grid = h('div', 'cards' + (c.flow ? ' flow' : ''));
      for (const [i, it] of c.items.entries()) {
        if (c.flow && i > 0) grid.appendChild(h('div', 'flow-arrow', '→', { 'aria-hidden': 'true' }));
        const card = h('div', 'card tone-' + (it.tone ?? 'neutral') + (it.actor ? ' actor-' + it.actor : ''));
        if (it.color) card.style.borderLeftColor = it.color;
        add(card, it.by ? h('div', 'actor-by', ACTOR_ICON[it.actor ?? 'human'] + ' ' + it.by) : null,
          h('div', 'card-title', it.title), it.body ? h('div', 'card-body', it.body) : null, it.tag ? h('span', 'tag', it.tag) : null);
        grid.appendChild(card);
      }
      return add(e, grid);
    }
    case 'agents': {
      const e = h('div', 'c');
      add(e, heading(c.title));
      // one line per agent is a roster, laid out side by side; a long exchange in which agents speak again reads
      // as a chat on stage: newest at the bottom, older lines scroll away (styles.css)
      const roster = new Set(c.items.map((a) => a.agent)).size === c.items.length;
      const list = h('div', 'agent-list' + (roster ? ` roster n${Math.min(c.items.length, 6)}` : c.items.length > 4 ? ' chat' : ''));
      for (const a of c.items) {
        const thinking = !a.mode && a.text.trim() === 'düşünüyor…';
        const card = h('div', 'agent' + (a.system ? ' system' : ' actor-agent') + ' tone-' + (a.tone ?? 'agent') + (thinking ? ' thinking' : ''));
        const color = a.color ?? 'var(--aria)';
        card.style.setProperty('--agent-color', color);
        const head = h('div', 'agent-head');
        add(head, h('span', 'agent-name', a.agent), a.status ? h('span', 'agent-status', a.status) : null,
          a.mode ? h('span', 'badge mode-' + a.mode, MODE_LABEL[a.mode] ?? a.mode) : null);
        add(card, head, a.how ? h('div', 'actor-by', ACTOR_ICON.agent + ' ' + a.how) : null, h('div', 'agent-text', a.text));
        list.appendChild(card);
      }
      return add(e, list);
    }
    case 'records': {
      const e = h('div', 'c');
      add(e, heading(c.title));
      for (const r of c.items) {
        const idx = Math.max(0, EVIDENCE.indexOf(r.evidence));
        const rec = h('div', 'record');
        const top = h('div', 'record-top');
        add(top, h('span', 'record-title', r.title), h('span', 'pill ev-' + idx, r.evidence), r.shared ? h('span', 'tag', 'ortak') : null);
        add(rec, top, r.body ? h('div', 'record-body', r.body) : null, r.owner ? h('div', 'record-owner', r.owner) : null);
        e.appendChild(rec);
      }
      return e;
    }
    case 'ladder': {
      const e = h('div', 'c');
      add(e, heading(c.title));
      for (const d of c.items) {
        const row = h('div', 'ladder' + (d.exceeded ? ' exceeded' : ''));
        const lab = h('div', 'ladder-label');
        add(lab, h('span', 'ladder-title', d.title), d.owner ? h('span', 'ladder-owner', d.owner) : null,
          d.redLine ? h('span', 'badge red', 'kırmızı çizgi') : null, d.exceeded ? h('span', 'badge red', 'bilgiden fazla yetki') : null);
        const bar = h('div', 'ladder-bar');
        for (let i = 0; i <= 4; i++) {
          const cell = h('div', 'lcell' + (i <= d.granted ? ' on' : '') + (i === d.ceiling ? ' ceiling' : ''), c.codes ? 'L' + i : '');
          bar.appendChild(cell);
        }
        const words = d.grantedText ? h('div', 'ladder-words') : null;
        if (words) add(words, h('span', '', 'Verdiğimiz: '), h('b', '', d.grantedText!), d.ceilingText ? h('span', '', ' · Bilgimizin taşıdığı: ') : null, d.ceilingText ? h('b', '', d.ceilingText) : null);
        add(row, lab, bar, words);
        e.appendChild(row);
      }
      return e;
    }
    case 'chain': {
      const e = h('div', 'c');
      add(e, heading(c.title));
      const list = h('div', 'chain');
      for (const s of c.items) {
        const st = s.ok === true ? 'ok' : s.ok === false ? 'fail' : 'pending';
        const row = h('div', 'step ' + st + (s.actor ? ' actor-' + s.actor : ''));
        const actor = s.actor === 'agent' ? '🤖 ajan' : s.actor === 'human' ? '👥 insan' : s.actor === 'both' ? '🤖👥 birlikte' : '';
        add(row, h('span', 'step-mark', st === 'ok' ? '✓' : st === 'fail' ? '✗' : '…'), h('span', 'step-label', s.label),
          actor ? h('span', 'badge', actor) : null, h('span', 'step-text', s.text));
        list.appendChild(row);
      }
      return add(e, list);
    }
    case 'tally': {
      const e = h('div', 'c');
      add(e, heading(c.title));
      for (const r of c.rows) {
        const row = h('div', 'tally-row');
        if (r.color) row.style.setProperty('--row-color', r.color);
        const total = r.parts.reduce((s, p) => s + p.count, 0) || 1;
        add(row, h('div', 'tally-label', r.label));
        const parts = h('div', 'tally-parts');
        for (const p of r.parts) {
          const part = h('div', 'tally-part' + (p.win ? ' win' : ''));
          const fill = h('div', 'tally-fill');
          fill.style.width = Math.round((p.count / total) * 100) + '%';
          add(part, fill, h('span', 'tally-text', p.text), h('span', 'tally-count', String(p.count)));
          parts.appendChild(part);
        }
        add(row, parts, r.note ? h('div', 'tally-note', r.note) : null);
        e.appendChild(row);
      }
      return e;
    }
    case 'progress': {
      const e = h('div', 'c');
      const head = h('div', 'progress-head');
      add(head, heading(c.title), c.closesAt ? countdown(c.closesAt) : null);
      add(e, head);
      for (const r of c.rows) {
        const row = h('div', 'prog-row');
        const pct = r.eligible > 0 ? Math.min(100, Math.round((r.received / r.eligible) * 100)) : 0;
        const bar = h('div', 'prog-bar');
        const fill = h('div', 'prog-fill');
        fill.style.width = pct + '%';
        fill.style.background = r.color ?? 'var(--accent)';
        bar.appendChild(fill);
        add(row, h('span', 'prog-label', r.label), bar, h('span', 'prog-num', `${r.received}/${r.eligible}`));
        e.appendChild(row);
      }
      if (c.synthetic && c.synthetic > 0) e.appendChild(h('div', 'synthetic', `sentetik oy: ${c.synthetic}`));
      return e;
    }
    case 'outcome': {
      const e = h('div', 'c c-outcome');
      const banner = h('div', 'banner cls-' + c.cls);
      add(banner, h('div', 'banner-label', c.label), h('div', 'banner-title', c.title));
      add(e, banner);
      if (c.lines && c.lines.length) e.appendChild(linesBlock(c.lines));
      return e;
    }
    case 'compare': {
      const e = h('div', 'c');
      add(e, heading(c.title));
      const cols = h('div', 'compare');
      for (const side of [c.left, c.right]) {
        const col = h('div', 'col');
        col.appendChild(h('h4', '', side.title));
        const ul = h('ul');
        for (const it of side.items) ul.appendChild(h('li', '', it));
        col.appendChild(ul);
        cols.appendChild(col);
      }
      return add(e, cols);
    }
    case 'qr': {
      const e = h('div', 'c c-qr');
      const url = c.url.startsWith('/') ? location.origin + c.url : c.url;
      const img = h('img', 'qr-img', undefined, { src: '/qr.svg?u=' + encodeURIComponent(url), alt: 'QR kod' });
      add(e, img, c.caption ? h('div', 'qr-caption', c.caption) : null, h('div', 'qr-url', url));
      return e;
    }
    case 'note':
      return h('div', 'c note tone-' + (c.tone ?? 'neutral'), c.text);
    case 'legend': {
      const e = h('div', 'c legend');
      add(e, h('span', 'lg-agent', '🤖 kesik çerçeve: ajanın yaptığı'), h('span', 'lg-human', '👥 düz çerçeve: insanların kararı'));
      return e;
    }
    case 'bands': {
      const e = h('div', 'c');
      add(e, heading(c.title));
      const span = Math.max(1e-9, c.max - c.min);
      const pos = (x: number) => `${Math.max(0, Math.min(100, ((x - c.min) / span) * 100))}%`;
      const u = c.unit ?? '';
      for (const it of c.items) {
        const row = h('div', 'band-row' + (it.win ? ' win' : ''));
        if (it.color) row.style.setProperty('--band-color', it.color);
        const lab = h('div', 'band-label');
        add(lab, h('span', 'band-name', it.label), h('span', 'band-range', `${u}${Math.round(it.lo)}–${Math.round(it.hi)}`), it.actual != null ? h('span', 'band-actual-v', `gerçekleşen ${u}${Math.round(it.actual)}`) : null);
        const track = h('div', 'band-track');
        const band = h('div', 'band-fill'); band.style.left = pos(it.lo); band.style.width = `calc(${pos(it.hi)} - ${pos(it.lo)} + 2px)`;
        track.appendChild(band);
        if (c.target != null) { const t = h('div', 'band-target'); t.style.left = pos(c.target); track.appendChild(t); }
        if (it.actual != null) { const a = h('div', 'band-actual'); a.style.left = pos(it.actual); track.appendChild(a); }
        add(row, lab, track, it.note ? h('div', 'band-note', it.note) : null);
        e.appendChild(row);
      }
      if (c.target != null && c.targetLabel) e.appendChild(h('div', 'band-legend', `│ ${c.targetLabel}`));
      return e;
    }
    case 'big': {
      const e = h('div', 'c c-big');
      add(e, h('div', 'big-text', c.text), c.sub ? h('div', 'big-sub', c.sub) : null);
      return e;
    }
    case 'badge': {
      const e = h('div', 'c c-badge');
      e.style.setProperty('--badge', c.color);
      const card = h('div', 'badge-card');
      const top = h('div', 'badge-top');
      add(top, h('span', 'badge-org', c.org), h('span', 'badge-chip', c.rows ? 'GÖREV KAYDI' : 'PERSONEL'));
      const mid = h('div', 'badge-mid');
      add(mid, h('div', 'badge-icon', c.icon), h('div', 'badge-who', undefined));
      add(mid.lastElementChild as HTMLElement, h('div', 'badge-dept', c.dept), h('div', 'badge-id', c.id));
      let rec: HTMLElement | null = null;
      if (c.rows?.length) {
        rec = h('div', 'badge-rec');
        if (c.title) rec.appendChild(h('div', 'badge-rec-t', c.title));
        for (const r of c.rows) { const row = h('div', 'badge-row'); add(row, h('span', 'badge-row-l', r.label), h('span', 'badge-row-v', r.value)); rec.appendChild(row); }
      }
      add(card, top, mid, c.role ? h('div', 'badge-role', c.role) : null, rec, c.stamp ? h('div', 'badge-stamp', c.stamp) : null, h('div', 'badge-strip'));
      return add(e, card, c.note ? h('div', 'badge-note', c.note) : null);
    }
  }
}

export type { Tone };

let onRenderError: (message: string) => void = () => {};
/** Where a component that fails to render is reported (the back office sees it). */
export function setRenderErrorHandler(fn: (message: string) => void): void { onRenderError = fn; }

/**
 * Motion for the stage: what is new in a scene enters in reading order, and agent and ARIA lines are typed,
 * one after another, so the room reads them as a conversation happening now. `seen` holds what is already
 * on screen in this scene (a vote count changing must not replay the scene). Everything is driven by timers,
 * not by CSS transitions, and `settle` forces the final state: a window that stops rendering can never
 * be left showing a half-faded or half-typed scene.
 */
export interface Motion { seen: Set<string>; settleTimer?: ReturnType<typeof setTimeout>; tick?: ReturnType<typeof setInterval>; typing: Typed[]; reveal: { el: HTMLElement; at: number }[] }
interface Typed { el: HTMLElement; full: string; start: number; dur: number }
export const newMotion = (): Motion => ({ seen: new Set(), typing: [], reveal: [] });

const ITEM_SEL = '.card, .agent, .record, .kpi, .step, .tally-row, .band-row, .ladder, .prog-row, .line, .compare .col';
const TYPE_SEL = '.agent:not(.system) .agent-text, .sp-aria .say';
const CPS = 55; // characters per second while typing

function animate(root: HTMLElement, m: Motion, ident: string[]): void {
  const t0 = performance.now();
  let cursor = 0;
  root.querySelectorAll<HTMLElement>(':scope > .c, :scope > .col > .c').forEach((comp, i) => {
    const key = ident[i];
    const fresh = !m.seen.has(key);
    m.seen.add(key);
    if (fresh) { comp.classList.add('enter'); comp.style.setProperty('--d', `${cursor}ms`); cursor += 260; }
    comp.querySelectorAll<HTMLElement>(ITEM_SEL).forEach((it) => {
      const typed = it.matches(TYPE_SEL) ? it : it.querySelector<HTMLElement>(TYPE_SEL);
      const k = `${key}|${(typed ?? it).textContent}`;
      if (m.seen.has(k)) return;
      m.seen.add(k);
      if (fresh || typed) {
        it.classList.add('enter'); it.style.setProperty('--d', `${cursor}ms`);
        // in a chat, a line not yet said takes no room, so the newest visible line is always the one being typed
        if (it.closest('.chat')) { it.classList.add('wait'); m.reveal.push({ el: it, at: t0 + cursor }); it.style.setProperty('--d', '0ms'); }
        cursor += 140;
      }
      if (typed && typed.textContent && typed.textContent.length > 3) {
        const full = typed.textContent;
        const dur = Math.min(2600, (full.length / CPS) * 1000);
        m.typing.push({ el: typed, full, start: t0 + cursor + 200, dur });
        typed.textContent = '';
        typed.classList.add('typing');
        cursor += dur + 350;
      }
    });
  });
  if (!m.typing.length && !cursor) return;
  const step = () => {
    const now = performance.now();
    m.reveal = m.reveal.filter((r) => { if (now < r.at) return true; r.el.classList.remove('wait'); return false; });
    m.typing = m.typing.filter((t) => {
      if (!t.el.isConnected) return false;
      const n = now < t.start ? 0 : Math.min(t.full.length, Math.ceil(((now - t.start) / t.dur) * t.full.length));
      if (t.el.textContent!.length !== n) t.el.textContent = t.full.slice(0, n);
      if (n >= t.full.length) { t.el.classList.remove('typing'); return false; }
      return true;
    });
    if (!m.typing.length && !m.reveal.length && m.tick) { clearInterval(m.tick); m.tick = undefined; }
  };
  if (!m.tick && (m.typing.length || m.reveal.length)) m.tick = setInterval(step, 33);
  clearTimeout(m.settleTimer);
  root.classList.remove('settled');
  m.settleTimer = setTimeout(() => settle(root, m), cursor + 1200);
}

/** Show everything in its final state now. */
export function settle(root: HTMLElement, m: Motion): void {
  for (const t of m.typing) { t.el.textContent = t.full; t.el.classList.remove('typing'); }
  for (const r of m.reveal) r.el.classList.remove('wait');
  m.typing = []; m.reveal = [];
  if (m.tick) { clearInterval(m.tick); m.tick = undefined; }
  root.classList.add('settled');
}

/**
 * Render a list of components. A component that throws is left out and reported; the rest still render,
 * so one bad item can never blank a screen. With `motion`, new parts enter and agent lines are typed.
 */
export function renderComponents(el: HTMLElement, components: Component[], motion?: Motion, measure?: () => void): void {
  const frag = document.createDocumentFragment();
  const ident: string[] = [];
  for (const [i, c] of (Array.isArray(components) ? components : []).entries()) {
    try { frag.appendChild(renderOne(c)); ident.push(`${i}:${c.kind}:${(c as { title?: string }).title ?? ''}`); } catch (e) { onRenderError(`render ${(c as { kind?: string })?.kind ?? '?'}: ${(e as Error).message}`); }
  }
  if (motion) settle(el, motion); // a repaint mid-animation: finish what was running before replacing it
  el.replaceChildren(frag);
  measure?.(); // layout is measured with everything in its final state, before anything is hidden or typed
  if (motion) { el.classList.add('motion'); try { animate(el, motion, ident); } catch (e) { settle(el, motion); onRenderError(`motion: ${(e as Error).message}`); } }
  else el.classList.remove('motion');
}
